import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from './use-toast';
import { Database } from '@/integrations/supabase/types';

type PaymentMethodType = Database['public']['Enums']['payment_method_type'];

export const usePaymentMethods = (authorId: string) => {
  const { toast } = useToast();

  return useQuery({
    queryKey: ['paymentMethods', authorId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('payment_methods')
        .select(`
          *,
          payment_details (*)
        `)
        .eq('author_id', authorId)
        .order('is_default', { ascending: false });
      
      if (error) {
        toast({
          title: 'Error loading payment methods',
          description: error.message,
          variant: 'destructive',
        });
        throw error;
      }
      
      return data;
    },
    enabled: !!authorId
  });
};

export const useCreatePaymentMethod = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      authorId,
      type,
      threshold,
      details
    }: {
      authorId: string;
      type: PaymentMethodType;
      threshold: number;
      details: Record<string, any>;
    }) => {
      // First create the payment method
      const paymentMethodData = {
        author_id: authorId,
        type,
        threshold,
        is_default: true
      };
      
      const { data: paymentMethod, error: methodError } = await supabase
        .from('payment_methods')
        .insert(paymentMethodData)
        .select()
        .single();
      
      if (methodError) {
        toast({
          title: 'Error creating payment method',
          description: methodError.message,
          variant: 'destructive',
        });
        throw methodError;
      }
      
      // Then create the payment details
      if (details && Object.keys(details).length > 0) {
        const paymentDetailsData = {
          author_id: authorId,
          payment_method_id: paymentMethod.id,
          ...details
        };
        
        const { data: paymentDetails, error: detailsError } = await supabase
          .from('payment_details')
          .insert(paymentDetailsData)
          .select()
          .single();
        
        if (detailsError) {
          // Cleanup the payment method if details failed
          await supabase
            .from('payment_methods')
            .delete()
            .eq('id', paymentMethod.id);
          
          toast({
            title: 'Error creating payment details',
            description: detailsError.message,
            variant: 'destructive',
          });
          throw detailsError;
        }
        
        return { paymentMethod, paymentDetails };
      }
      
      return { paymentMethod };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['paymentMethods', variables.authorId] });
      queryClient.invalidateQueries({ queryKey: ['author', variables.authorId] });
      toast({
        title: 'Success',
        description: 'Payment method has been created.',
      });
    }
  });
};

export const useUpdatePaymentMethod = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      methodId,
      authorId,
      updates,
      details
    }: {
      methodId: string;
      authorId: string;
      updates?: Record<string, any>;
      details?: Record<string, any>;
    }) => {
      // Update the payment method if updates provided
      if (updates && Object.keys(updates).length > 0) {
        const { error: methodError } = await supabase
          .from('payment_methods')
          .update(updates)
          .eq('id', methodId);
        
        if (methodError) {
          toast({
            title: 'Error updating payment method',
            description: methodError.message,
            variant: 'destructive',
          });
          throw methodError;
        }
      }
      
      // Update payment details if provided
      if (details && Object.keys(details).length > 0) {
        // Check if details exist
        const { data: existingDetails, error: checkError } = await supabase
          .from('payment_details')
          .select('id')
          .eq('payment_method_id', methodId)
          .maybeSingle();
        
        if (checkError) {
          console.error('Error checking payment details:', checkError);
        }
        
        if (existingDetails) {
          // Update existing details
          const { error: detailsError } = await supabase
            .from('payment_details')
            .update(details)
            .eq('id', existingDetails.id);
          
          if (detailsError) {
            toast({
              title: 'Error updating payment details',
              description: detailsError.message,
              variant: 'destructive',
            });
            throw detailsError;
          }
        } else {
          // Create new details
          const paymentDetailsData = {
            author_id: authorId,
            payment_method_id: methodId,
            ...details
          };
          
          const { error: createError } = await supabase
            .from('payment_details')
            .insert(paymentDetailsData);
          
          if (createError) {
            toast({
              title: 'Error creating payment details',
              description: createError.message,
              variant: 'destructive',
            });
            throw createError;
          }
        }
      }
      
      return { success: true };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['paymentMethods', variables.authorId] });
      queryClient.invalidateQueries({ queryKey: ['author', variables.authorId] });
      toast({
        title: 'Success',
        description: 'Payment method has been updated.',
      });
    }
  });
};

// Rename to useSetDefaultPaymentMethod for clarity
export const useSetDefaultPaymentMethod = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      methodId,
      authorId
    }: {
      methodId: string;
      authorId: string;
    }) => {
      // First set all methods to non-default
      const { error: resetError } = await supabase
        .from('payment_methods')
        .update({ is_default: false })
        .eq('author_id', authorId);
      
      if (resetError) {
        toast({
          title: 'Error resetting payment methods',
          description: resetError.message,
          variant: 'destructive',
        });
        throw resetError;
      }
      
      // Then set the selected method as default
      const { data, error } = await supabase
        .from('payment_methods')
        .update({ is_default: true })
        .eq('id', methodId)
        .select()
        .single();
      
      if (error) {
        toast({
          title: 'Error setting default payment method',
          description: error.message,
          variant: 'destructive',
        });
        throw error;
      }
      
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['paymentMethods', variables.authorId] });
      queryClient.invalidateQueries({ queryKey: ['author', variables.authorId] });
      toast({
        title: 'Success',
        description: 'Default payment method has been updated.',
      });
    }
  });
};

export const useDeletePaymentMethod = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      methodId,
      authorId
    }: {
      methodId: string;
      authorId: string;
    }) => {
      const { error } = await supabase
        .from('payment_methods')
        .delete()
        .eq('id', methodId);
      
      if (error) {
        toast({
          title: 'Error deleting payment method',
          description: error.message,
          variant: 'destructive',
        });
        throw error;
      }
      
      return { success: true };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['paymentMethods', variables.authorId] });
      queryClient.invalidateQueries({ queryKey: ['author', variables.authorId] });
      toast({
        title: 'Success',
        description: 'Payment method has been deleted.',
      });
    }
  });
};
